import { useState } from 'react';
import { Link, useParams, useSearchParams, Navigate } from 'react-router-dom';
import {
  Search,
  Users,
  ScanLine,
  HardHat,
  Wrench,
  Truck,
  Stethoscope,
  type LucideIcon,
} from 'lucide-react';
import { STATIONS, stationLabel, type KitItem, type Station } from '@hcc/types';
import { useCurrentUser } from '../../hooks/useAuth';
import { usePeopleList, type PersonApi } from '../../hooks/usePeople';
import { useEffectiveKits } from '../../hooks/useKitTemplates';
import { useAssetsList } from '../../hooks/useAssets';
import { useCurrentScope } from '../../hooks/useMobilisations';
import { KitIssueDrawer } from '../../components/kit/KitIssueDrawer';
import { PersonNotesDialog } from '../../components/person/PersonNotesDialog';
import { SkeletonList } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { ToIssueSection } from './ToIssueSection';
import { IssuedSection } from './IssuedSection';
import { StatTile } from './StatTile';

const STATION_ICONS: Record<Station, LucideIcon> = {
  IT: ScanLine,
  PPE: HardHat,
  Tools: Wrench,
  Fleet: Truck,
  DBSHR: Stethoscope,
};

/**
 * Per-station view (CLAUDE.md 6.2). Shows arrived attendees, their
 * outstanding items for THIS station per the kit template assigned
 * to their position, and a kit-issue flow that captures a signature.
 *
 * URL: /stations/IT, /stations/PPE, etc. Station slug must match the
 * Station enum exactly (case-sensitive); unknown slugs redirect home.
 *
 * The "outstanding items" view uses the kit template as the expected
 * list and the events history as the issued list; differences are
 * still outstanding. For phase 1 item 9 we rely on the dev exporting
 * the kit_template for the person's position; if no template exists
 * the page shows "no expected items" so the operator can free-form a
 * KitIssued event with whatever was actually handed over.
 */
export function StationView() {
  const { station } = useParams<{ station: string }>();
  const [searchParams] = useSearchParams();
  const preday = searchParams.get('preday') === 'true';
  const { data: user, isLoading: meLoading } = useCurrentUser();

  if (!station || !STATIONS.includes(station as Station)) {
    return <Navigate to="/" replace />;
  }
  const stationKey = station as Station;

  const [q, setQ] = useState('');
  // Under the ongoing onboarding programme scope there is no reception
  // check-in, so the arrived gate would hide everyone. Behaves like
  // pre-day mode: the whole cohort is listed and issuance works the same.
  const scope = useCurrentScope();
  const onboardingScope = scope?.kind === 'onboarding';
  // Pre-day mode: lift the arrived filter so the operator can issue
  // kit to attendees who haven't checked in yet. The KitIssued events
  // written here are indistinguishable from on-the-day issuances and
  // flow through to the inductee's record naturally.
  const list = usePeopleList(q, preday || onboardingScope ? undefined : true);
  const effectiveKits = useEffectiveKits();
  // Snapshots, not live-list lookups: any socket broadcast refetches the
  // list, and if that refetch drops the person (undo check-in on the
  // wrong row, archive) a derived lookup would unmount the drawer mid
  // signature and discard everything entered. The drawer keeps working
  // on the snapshot; its own usePerson query stays live for flags.
  const [activePerson, setActivePerson] = useState<PersonApi | null>(null);
  const [notesPerson, setNotesPerson] = useState<PersonApi | null>(null);
  const [showIssued, setShowIssued] = useState(false);
  // Default each desk to just the people it needs to find. Toggle on to
  // reveal everyone on site (for ad-hoc issuance to someone with no kit
  // allocated here).
  const [showUnallocated, setShowUnallocated] = useState(false);
  // All assets tagged to this station, so we can tell who has a specific
  // item pre-assigned even when their role has no kit template.
  const stationAssets = useAssetsList({ station: stationKey });

  if (meLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please{' '}
        <Link to="/login" className="underline">
          sign in
        </Link>
        .
      </main>
    );
  }
  if (user.roles.length === 0) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Your account has no permissions yet. Ask an admin.
      </main>
    );
  }

  // Effective kit per role: the category baseline merged with the role's
  // own template, resolved server-side. Keyed by role name, which mirrors
  // people.position.
  const kitByPosition = new Map<string, KitItem[]>();
  for (const k of effectiveKits.data ?? []) kitByPosition.set(k.role_name, k.items);

  // "Kit allocated at this station" = their effective kit lists an
  // item here, or they have a specific asset pre-assigned to them here.
  const positionsWithStationKit = new Set<string>();
  for (const k of effectiveKits.data ?? []) {
    if (k.items.some((it) => it.station === stationKey)) {
      positionsWithStationKit.add(k.role_name);
    }
  }
  const preAssignedPersonIds = new Set<string>();
  for (const a of stationAssets.data ?? []) {
    if (a.assigned_to_person_id) preAssignedPersonIds.add(a.assigned_to_person_id);
  }
  const hasStationAllocation = (p: PersonApi) =>
    (p.position != null && positionsWithStationKit.has(p.position)) ||
    preAssignedPersonIds.has(p.id);

  const activeKitItems = activePerson?.position
    ? (kitByPosition.get(activePerson.position) ?? null)
    : null;

  const StationIcon = STATION_ICONS[stationKey];

  // Split the arrived list into "still to issue" vs "already issued
  // at this station". The flag comes from PersonApi.stations which
  // the server derives from KitIssued events. Searching expands the
  // bottom section automatically so a match there isn't hidden.
  const everyone = list.data ?? [];
  const toIssueAll = everyone.filter((p) => !p.stations?.[stationKey]?.complete);
  const issued = everyone.filter((p) => p.stations?.[stationKey]?.complete);
  const searching = q.trim().length > 0;
  const issuedExpanded = searching || showIssued;

  // The work list defaults to people with kit allocated here. Searching
  // or the "show all" toggle lifts the filter so ad-hoc issuance to
  // anyone on site stays one tap away.
  const toIssueAllocated = toIssueAll.filter(hasStationAllocation);
  const toIssue = searching || showUnallocated ? toIssueAll : toIssueAllocated;
  const unallocatedHidden = toIssueAll.length - toIssueAllocated.length;

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
        <header className="flex items-start gap-4">
          <div
            className={`flex h-12 w-12 flex-none items-center justify-center rounded-token-sm ${preday ? 'bg-warning-soft/20 text-warning-soft-fg' : 'bg-accent/15 text-accent'}`}
          >
            <StationIcon className="h-6 w-6" strokeWidth={2} />
          </div>
          <div>
            <div
              className={`text-xs font-semibold uppercase tracking-wider ${preday ? 'text-warning-soft-fg' : 'text-accent'}`}
            >
              {preday ? 'Pre-day issuance' : onboardingScope ? 'Day-to-day onboarding' : 'Station'}
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-fg sm:text-3xl">
              {stationLabel(stationKey)}
              {preday ? (
                <span className="ml-2 text-base font-medium text-warning-soft-fg">(early)</span>
              ) : null}
            </h1>
            <p className="mt-1 text-sm text-fg-muted">
              {preday
                ? "Issue kit to attendees before they check in on the day. They'll receive their signed PDF receipt by email — paper isn't an option when they're not on site."
                : onboardingScope
                  ? 'Issue kit to any starter or existing staff member on the onboarding programme. No check-in needed; the signed PDF receipt is emailed to them automatically.'
                  : 'Tap an arrived attendee to record what was issued and capture their signature.'}
            </p>
          </div>
        </header>

        {preday ? (
          <div className="mt-4 rounded-token-sm border border-warning/30 bg-warning-soft/15 px-4 py-3 text-sm text-warning-soft-fg">
            <strong className="font-semibold">Pre-day mode.</strong> The arrival filter is off —
            anyone in the cohort shows up. The signed PDF receipt is emailed to the attendee
            automatically (the per-station email toggle is bypassed for pre-day issuance). Items
            here flow through to their record and appear as already issued when they arrive on the
            day.
          </div>
        ) : null}

        <div className="relative mt-6">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-fg-dim" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={
              preday || onboardingScope ? 'Search attendees...' : 'Search arrived attendees...'
            }
            className="block h-12 w-full rounded-token-lg border border-line-strong/20 bg-surface pl-11 pr-3 text-base text-fg shadow-token-sm transition-colors focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/30"
          />
        </div>

        {/* Summary tiles — operator's at-a-glance scoreboard for this
            station. Recomputes live from the same usePeopleList query
            that drives the lists below; Socket.IO event broadcasts
            invalidate the cache so issuing kit anywhere updates here. */}
        <div className="mt-4 grid grid-cols-3 gap-3">
          <StatTile
            label={onboardingScope ? 'In programme' : 'On site'}
            value={everyone.length}
            tone="slate"
          />
          <StatTile label="To issue" value={toIssue.length} tone="info" />
          <StatTile label="Issued" value={issued.length} tone="success" />
        </div>

        {list.isLoading ? (
          <div className="mt-4 overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
            <SkeletonList rows={6} />
          </div>
        ) : !list.data || list.data.length === 0 ? (
          <div className="mt-4 overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
            {q ? (
              <EmptyState
                icon={<Search className="h-6 w-6" />}
                title="No matches"
                description={
                  preday
                    ? `Nothing for "${q}". Make sure they're imported as an attendee for this mobilisation.`
                    : onboardingScope
                      ? `Nothing for "${q}". Make sure they've been added as a person (they join the onboarding programme automatically).`
                      : `Nothing for "${q}". Make sure they've checked in at reception.`
                }
              />
            ) : onboardingScope ? (
              <EmptyState
                icon={<Users className="h-6 w-6" />}
                title="No one on the onboarding programme yet"
                description="People appear here as soon as they're imported or added, no check-in needed."
              />
            ) : (
              <EmptyState
                icon={<Users className="h-6 w-6" />}
                title="No arrivals yet"
                description="Attendees show up here as soon as reception checks them in. Real-time, no refresh needed."
              />
            )}
          </div>
        ) : (
          <>
            <ToIssueSection
              station={stationKey}
              people={toIssue}
              searching={searching}
              unallocatedHidden={unallocatedHidden}
              showUnallocated={showUnallocated}
              onToggleUnallocated={() => setShowUnallocated((v) => !v)}
              onIssue={setActivePerson}
              onNotes={setNotesPerson}
            />
            <IssuedSection
              station={stationKey}
              people={issued}
              searching={searching}
              expanded={issuedExpanded}
              onToggleExpanded={() => setShowIssued((v) => !v)}
              onReissue={setActivePerson}
              onNotes={setNotesPerson}
            />
          </>
        )}
      </div>

      {activePerson ? (
        // Key on the person so switching attendees fully remounts the drawer:
        // its one-time size seeding resets, and one person's kit can never
        // carry over to another.
        <KitIssueDrawer
          key={activePerson.id}
          station={stationKey}
          person={activePerson}
          templateItems={activeKitItems}
          onClose={() => setActivePerson(null)}
        />
      ) : null}

      {notesPerson ? (
        <PersonNotesDialog
          open
          personId={notesPerson.id}
          personName={notesPerson.full_name}
          station={stationKey}
          onClose={() => setNotesPerson(null)}
        />
      ) : null}
    </main>
  );
}
